import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Async, FieldType, FieldValue, FileField, Form, FormField, O, option } from "../../../../../features/ui"
import { Operation } from "../../../../operations"
import { Delivery, DeliveryOrder, DeliveryStatus } from "../../../state/types"
import { CompleteShipmentItemRow } from "../../types"
import { useActions } from "./actions"
import { useDataTypesHooks, useDescribeHooks } from "../../../../../features/data-types"
import { TableName } from "../../../../../tables"
import { CompleteShipmentItemsTable } from "../complete-shipment-items-table/completeShipmentItemsTable"
import { fileHelpers } from "../../../../../features/files"
import { addressHelpers } from "../../../../../features/address"
import { deliveryHelpers } from "../../../hooks/deliveryHooks"
import { useDeliveryOrderHooks } from "../../../hooks/deliveryOrderHooks"
import { toDate } from "../../../../../utils/typeUtils"

type Props = {
    deliveries: Delivery[]
    deliveryOrders: DeliveryOrder[]
    operations: Operation[]
}

const buildItemRows = (
    delivery: Delivery,
    deliveryOrder: DeliveryOrder,
    operation: Operation
): CompleteShipmentItemRow[] =>
    delivery.items
        .filter(item => {
            const deliveryOrderItem = deliveryOrder.items.find(deliveryOrderItem => deliveryOrderItem.itemId === item.itemId)!
            return deliveryOrderItem.deliveryStats.dispatched > 0
        })
        .map(item => {
            const operationItem = operation.items.find(operationItem => operationItem.id === item.itemId)!
            return {
                itemId: item.itemId,
                productId: operationItem.productId,
                lotId: operationItem.lotId,
                dispatched: item.quantity,
                deliveredQuantity: item.quantity,
                notDeliveredQuantity: 0
            }
        })

export const CompleteDeliveryForm = (props: Props) => {
    const { deliveries, deliveryOrders, operations } = props

    const { describeAddress } = addressHelpers
    const { describeDelivery } = deliveryHelpers
    const { deliveryNoteFileName2 } = fileHelpers
    const { getDeliveryAddress } = useDeliveryOrderHooks()
    const { describe } = useDescribeHooks()
    const { formatDate } = useDataTypesHooks()
    const navigate = useNavigate()
    const preselectedDeliveryId = useLocation().state?.deliveryId as O<string>

    const { completeDelivery } = useActions()

    const eligibleDeliveries = useMemo(() =>
        deliveries
            .filter(delivery => delivery.deliveryStatus !== DeliveryStatus.DELIVERED)
            .sort((delivery_A, delivery_B) => delivery_A.date.localeCompare(delivery_B.date)),
    [deliveries])

    const [selectedDelivery, setSelectedDelivery] = useState<O<Delivery>>(
        eligibleDeliveries.find(delivery => delivery.id === preselectedDeliveryId) || eligibleDeliveries[0]
    )
    const [deliveryNoteFile, setDeliveryNoteFile] = useState<File>()
    const [itemRows, setItemRows] = useState<CompleteShipmentItemRow[]>([])

    const findDeliveryOrder = (delivery: Delivery) => deliveryOrders.find(deliveryOrder => deliveryOrder.id === delivery.orderId)!
    const findOperation = (delivery: Delivery) => operations.find(operation => operation.id === delivery.operationId)!

    useEffect(() => {
        if (selectedDelivery) {
            setItemRows(buildItemRows(selectedDelivery, findDeliveryOrder(selectedDelivery), findOperation(selectedDelivery)))
            setDeliveryNoteFile(undefined)
        }
    }, [selectedDelivery, deliveryOrders, operations])

    const deliveryOptions = useMemo(() =>
        eligibleDeliveries.map(delivery => option(delivery.id, describeDelivery(delivery, findDeliveryOrder(delivery))))
    , [eligibleDeliveries, deliveryOrders])

    const onChangeDelivery = (value?: FieldValue) => {
        const delivery = eligibleDeliveries.find(delivery => delivery.id === value?.toString())
        setSelectedDelivery(delivery)
    }

    const totalDelivered = useMemo(
        () => itemRows.reduce((sum, item) => sum + item.deliveredQuantity + item.notDeliveredQuantity, 0)
    , [itemRows])

    const fields: FormField[] = [
        {
            name: 'deliveryId',
            type: FieldType.SELECT,
            table: TableName.DELIVERIES2,
            label: 'Remitos',
            value: selectedDelivery?.id,
            options: deliveryOptions,
            onChange: onChangeDelivery,
            renderInputLabel: (optionValue: string) => {
                const delivery = eligibleDeliveries.find(delivery => delivery.id === optionValue)!
                return `${describeDelivery(delivery, findDeliveryOrder(delivery))} - ${formatDate(toDate(delivery.date))}`
            }
        },
        {
            name: 'deliveryNoteUrl',
            type: FieldType.FILE,
            label: 'Remito Firmado',
            hint: 'Formato: PDF',
            render: () => (
                <FileField
                    id="deliveryNoteUrl"
                    fileName={selectedDelivery ? deliveryNoteFileName2(selectedDelivery, findDeliveryOrder(selectedDelivery)) : ''}
                    value={deliveryNoteFile}
                    disabled={!selectedDelivery}
                    onChange={setDeliveryNoteFile}
                />
            )
        }
    ]
    if (selectedDelivery) {
        fields.push({
            name: 'operationCode',
            type: FieldType.TEXT,
            label: 'Operación',
            render: () => <>{describe(selectedDelivery.table, findOperation(selectedDelivery))}</>
        },
        {
            name: 'deliveryAddress',
            type: FieldType.TEXT,
            label: 'Dirección de Entrega',
            render: () => selectedDelivery ? (
                <Async
                    loadFn={async () => {
                        const address = await getDeliveryAddress(selectedDelivery)!
                        return <>{describeAddress(address)}</>
                    }}
                />
            ) : <></>
        },
        {
            name: 'items',
            type: FieldType.TABLE,
            render: () => selectedDelivery ? (
                <CompleteShipmentItemsTable
                    key={selectedDelivery.id}
                    operation={findOperation(selectedDelivery)}
                    defaultRows={itemRows}
                    onChange={setItemRows}
                />
            ) : <></>
        })
    }

    const onSubmit = async () => {
        await completeDelivery(selectedDelivery!.id, itemRows, deliveryNoteFile!)
    }

    const onCancel = () => navigate(-1)

    return (
        <Form
            formId="complete-delivery-form"
            fields={fields}
            createLabel="Completar Envío"
            disableSubmit={!selectedDelivery || totalDelivered === 0}
            onSubmit={onSubmit}
            onCancel={onCancel}
        />
    )
}
